/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  AddCredentialsToDashboardPathParameters,
  AddDataChecksToDashboardPathParameters,
  CreateDashboardSchedulePathParameters,
  DeleteDashboardPathParameters,
  DeleteDashboardSchedulePathParameters,
  ExecuteBatchDashboardAnalyticsQueriesPathParameters,
  ExecuteDashboardAnalyticsQueryPathParameters,
  ExecuteDashboardPathParameters,
  GetAllDashboardsParams,
  GetAllErrorTrackingPathParameters,
  GetDashboardByIdParams,
  GetDashboardByIdPathParameters,
  GetDashboardCredentialsPathParameters,
  GetDashboardDataCheckRecordsPathParameters,
  GetDashboardDataChecksPathParameters,
  GetDashboardDataSourceImportStatusPathParameters,
  GetDashboardDataSourceQueryStatusPathParameters,
  GetDashboardExecutionsParams,
  GetDashboardExecutionsPathParameters,
  GetDashboardSchedulesPathParameters,
  GetDashboardStorageStatusParams,
  GetDashboardStorageStatusPathParameters,
  ImportDashboardDataSourceFilePathParameters,
  MaterializeDashboardDataSourceQueryPathParameters,
  RemoveCredentialsFromDashboardPathParameters,
  TLAddCredentialsRequest,
  TLAnalyticsBatchQueryRequest,
  TLAnalyticsBatchQueryResponse,
  TLAnalyticsQueryRequest,
  TLAnalyticsQueryResponse,
  TLDashboardConfigResponse,
  TLDashboardConfigUpdateRequest,
  TLDashboardCreateRequest,
  TLDashboardCredentialMappingsResponse,
  TLDashboardDataCheckAddRequest,
  TLDashboardDataChecksResult,
  TLDashboardResponse,
  TLDashboardScheduleCreateRequest,
  TLDashboardScheduleResponse,
  TLDashboardSchedulesResponse,
  TLDashboardScheduleUpdateRequest,
  TLDashboardsResponse,
  TLDashboardStorageStatusResponse,
  TLDashboardUpdateRequest,
  TLDataChecksResponse,
  TLDataSourceImportFileResponse,
  TLDataSourceQueryRequest,
  TLDataSourceQueryResponse,
  TLExecuteDashboardResponse,
  TLFoundryTableImportResponse,
  TLGetAllErrorTrackingResponse,
  TLGetDashboardDataChecksResponse,
  TLGetDashboardExecutionsResponse,
  TLHTTPValidationError,
  TLImportDashboardDataSourceFileHarmonizationRequest,
  TLQueryStatusResponse,
  TLRemoveCredentialsRequest,
  TLSuccessResponse,
  UpdateDashboardConfigPathParameters,
  UpdateDashboardPathParameters,
  UpdateDashboardSchedulePathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get All Dashboards
 */
export const getAllDashboards = (
  params?: GetAllDashboardsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardsResponse>(
    { url: `/v2/data-harmonization/dashboards`, method: 'GET', params, signal },
    options,
  );
};

export const getGetAllDashboardsQueryKey = (params?: GetAllDashboardsParams) => {
  return ['v2', 'data-harmonization', 'dashboards', ...(params ? [params] : [])] as const;
};

export const getGetAllDashboardsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllDashboards>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllDashboardsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDashboards>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllDashboardsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllDashboards>>> = ({ signal }) =>
    getAllDashboards(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllDashboards>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllDashboardsQueryResult = NonNullable<Awaited<ReturnType<typeof getAllDashboards>>>;
export type GetAllDashboardsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetAllDashboards<
  TData = Awaited<ReturnType<typeof getAllDashboards>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetAllDashboardsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDashboards>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllDashboards>>,
          TError,
          Awaited<ReturnType<typeof getAllDashboards>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllDashboards<
  TData = Awaited<ReturnType<typeof getAllDashboards>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllDashboardsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDashboards>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllDashboards>>,
          TError,
          Awaited<ReturnType<typeof getAllDashboards>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllDashboards<
  TData = Awaited<ReturnType<typeof getAllDashboards>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllDashboardsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDashboards>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Dashboards
 */

export function useGetAllDashboards<
  TData = Awaited<ReturnType<typeof getAllDashboards>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllDashboardsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDashboards>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllDashboardsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Dashboards
 */
export const prefetchGetAllDashboardsQuery = async <
  TData = Awaited<ReturnType<typeof getAllDashboards>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetAllDashboardsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDashboards>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllDashboardsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Dashboards
 */
export const invalidateGetAllDashboards = async (
  queryClient: QueryClient,
  params?: GetAllDashboardsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAllDashboardsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Dashboard
 */
export const createDashboard = (
  tLDashboardCreateRequest: BodyType<TLDashboardCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardResponse>(
    {
      url: `/v2/data-harmonization/dashboards`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLDashboardCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateDashboardQueryKey = (tLDashboardCreateRequest?: BodyType<TLDashboardCreateRequest>) => {
  return ['POST', 'v2', 'data-harmonization', 'dashboards', tLDashboardCreateRequest] as const;
};

export const getCreateDashboardQueryOptions = <
  TData = Awaited<ReturnType<typeof createDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDashboardCreateRequest: BodyType<TLDashboardCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateDashboardQueryKey(tLDashboardCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createDashboard>>> = ({ signal }) =>
    createDashboard(tLDashboardCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createDashboard>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateDashboardQueryResult = NonNullable<Awaited<ReturnType<typeof createDashboard>>>;
export type CreateDashboardQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateDashboard<
  TData = Awaited<ReturnType<typeof createDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDashboardCreateRequest: BodyType<TLDashboardCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboard>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createDashboard>>,
          TError,
          Awaited<ReturnType<typeof createDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateDashboard<
  TData = Awaited<ReturnType<typeof createDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDashboardCreateRequest: BodyType<TLDashboardCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboard>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createDashboard>>,
          TError,
          Awaited<ReturnType<typeof createDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateDashboard<
  TData = Awaited<ReturnType<typeof createDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDashboardCreateRequest: BodyType<TLDashboardCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Dashboard
 */

export function useCreateDashboard<
  TData = Awaited<ReturnType<typeof createDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDashboardCreateRequest: BodyType<TLDashboardCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateDashboardQueryOptions(tLDashboardCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Dashboard
 */
export const prefetchCreateDashboardQuery = async <
  TData = Awaited<ReturnType<typeof createDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLDashboardCreateRequest: BodyType<TLDashboardCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateDashboardQueryOptions(tLDashboardCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Dashboard
 */
export const invalidateCreateDashboard = async (
  queryClient: QueryClient,
  tLDashboardCreateRequest: BodyType<TLDashboardCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateDashboardQueryKey(tLDashboardCreateRequest) }, options);

  return queryClient;
};

/**
 * @summary Delete Dashboard
 */
export const deleteDashboard = (
  { dashboardId }: DeleteDashboardPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/dashboards/${dashboardId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteDashboardQueryKey = ({ dashboardId }: DeleteDashboardPathParameters) => {
  return ['DELETE', 'v2', 'data-harmonization', 'dashboards', dashboardId] as const;
};

export const getDeleteDashboardQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: DeleteDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteDashboardQueryKey({ dashboardId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteDashboard>>> = ({ signal }) =>
    deleteDashboard({ dashboardId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteDashboard>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteDashboardQueryResult = NonNullable<Awaited<ReturnType<typeof deleteDashboard>>>;
export type DeleteDashboardQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteDashboard<
  TData = Awaited<ReturnType<typeof deleteDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDashboardPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboard>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteDashboard>>,
          TError,
          Awaited<ReturnType<typeof deleteDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteDashboard<
  TData = Awaited<ReturnType<typeof deleteDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboard>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteDashboard>>,
          TError,
          Awaited<ReturnType<typeof deleteDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteDashboard<
  TData = Awaited<ReturnType<typeof deleteDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Dashboard
 */

export function useDeleteDashboard<
  TData = Awaited<ReturnType<typeof deleteDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: DeleteDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteDashboardQueryOptions({ dashboardId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Dashboard
 */
export const prefetchDeleteDashboardQuery = async <
  TData = Awaited<ReturnType<typeof deleteDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: DeleteDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteDashboardQueryOptions({ dashboardId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Dashboard
 */
export const invalidateDeleteDashboard = async (
  queryClient: QueryClient,
  { dashboardId }: DeleteDashboardPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteDashboardQueryKey({ dashboardId }) }, options);

  return queryClient;
};

/**
 * @summary Get Dashboard By Id
 */
export const getDashboardById = (
  { dashboardId }: GetDashboardByIdPathParameters,
  params?: GetDashboardByIdParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardResponse>(
    { url: `/v2/data-harmonization/dashboards/${dashboardId}`, method: 'GET', params, signal },
    options,
  );
};

export const getGetDashboardByIdQueryKey = (
  { dashboardId }: GetDashboardByIdPathParameters,
  params?: GetDashboardByIdParams,
) => {
  return ['v2', 'data-harmonization', 'dashboards', dashboardId, ...(params ? [params] : [])] as const;
};

export const getGetDashboardByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboardById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardByIdPathParameters,
  params?: GetDashboardByIdParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardByIdQueryKey({ dashboardId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardById>>> = ({ signal }) =>
    getDashboardById({ dashboardId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDashboardById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDashboardByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardById>>>;
export type GetDashboardByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDashboardById<
  TData = Awaited<ReturnType<typeof getDashboardById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardByIdPathParameters,
  params: undefined | GetDashboardByIdParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardById>>,
          TError,
          Awaited<ReturnType<typeof getDashboardById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardById<
  TData = Awaited<ReturnType<typeof getDashboardById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardByIdPathParameters,
  params?: GetDashboardByIdParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardById>>,
          TError,
          Awaited<ReturnType<typeof getDashboardById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardById<
  TData = Awaited<ReturnType<typeof getDashboardById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardByIdPathParameters,
  params?: GetDashboardByIdParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Dashboard By Id
 */

export function useGetDashboardById<
  TData = Awaited<ReturnType<typeof getDashboardById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardByIdPathParameters,
  params?: GetDashboardByIdParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDashboardByIdQueryOptions({ dashboardId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Dashboard By Id
 */
export const prefetchGetDashboardByIdQuery = async <
  TData = Awaited<ReturnType<typeof getDashboardById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: GetDashboardByIdPathParameters,
  params?: GetDashboardByIdParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDashboardByIdQueryOptions({ dashboardId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Dashboard By Id
 */
export const invalidateGetDashboardById = async (
  queryClient: QueryClient,
  { dashboardId }: GetDashboardByIdPathParameters,
  params?: GetDashboardByIdParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetDashboardByIdQueryKey({ dashboardId }, params) }, options);

  return queryClient;
};

/**
 * @summary Update Dashboard
 */
export const updateDashboard = (
  { dashboardId }: UpdateDashboardPathParameters,
  tLDashboardUpdateRequest: BodyType<TLDashboardUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    {
      url: `/v2/data-harmonization/dashboards/${dashboardId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLDashboardUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateDashboardQueryKey = (
  { dashboardId }: UpdateDashboardPathParameters,
  tLDashboardUpdateRequest?: BodyType<TLDashboardUpdateRequest>,
) => {
  return ['PATCH', 'v2', 'data-harmonization', 'dashboards', dashboardId, tLDashboardUpdateRequest] as const;
};

export const getUpdateDashboardQueryOptions = <
  TData = Awaited<ReturnType<typeof updateDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: UpdateDashboardPathParameters,
  tLDashboardUpdateRequest: BodyType<TLDashboardUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateDashboardQueryKey({ dashboardId }, tLDashboardUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateDashboard>>> = ({ signal }) =>
    updateDashboard({ dashboardId }, tLDashboardUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateDashboard>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateDashboardQueryResult = NonNullable<Awaited<ReturnType<typeof updateDashboard>>>;
export type UpdateDashboardQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateDashboard<
  TData = Awaited<ReturnType<typeof updateDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDashboardPathParameters,
  tLDashboardUpdateRequest: BodyType<TLDashboardUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboard>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDashboard>>,
          TError,
          Awaited<ReturnType<typeof updateDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDashboard<
  TData = Awaited<ReturnType<typeof updateDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDashboardPathParameters,
  tLDashboardUpdateRequest: BodyType<TLDashboardUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboard>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDashboard>>,
          TError,
          Awaited<ReturnType<typeof updateDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDashboard<
  TData = Awaited<ReturnType<typeof updateDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDashboardPathParameters,
  tLDashboardUpdateRequest: BodyType<TLDashboardUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Dashboard
 */

export function useUpdateDashboard<
  TData = Awaited<ReturnType<typeof updateDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: UpdateDashboardPathParameters,
  tLDashboardUpdateRequest: BodyType<TLDashboardUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateDashboardQueryOptions({ dashboardId }, tLDashboardUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Dashboard
 */
export const prefetchUpdateDashboardQuery = async <
  TData = Awaited<ReturnType<typeof updateDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: UpdateDashboardPathParameters,
  tLDashboardUpdateRequest: BodyType<TLDashboardUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateDashboardQueryOptions({ dashboardId }, tLDashboardUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Dashboard
 */
export const invalidateUpdateDashboard = async (
  queryClient: QueryClient,
  { dashboardId }: UpdateDashboardPathParameters,
  tLDashboardUpdateRequest: BodyType<TLDashboardUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateDashboardQueryKey({ dashboardId }, tLDashboardUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Execute Dashboard Analytics Query
 */
export const executeDashboardAnalyticsQuery = (
  { dashboardId }: ExecuteDashboardAnalyticsQueryPathParameters,
  tLAnalyticsQueryRequest: BodyType<TLAnalyticsQueryRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAnalyticsQueryResponse>(
    {
      url: `/v2/data-harmonization/dashboards/${dashboardId}/analytics/query`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLAnalyticsQueryRequest,
      signal,
    },
    options,
  );
};

export const getExecuteDashboardAnalyticsQueryQueryKey = (
  { dashboardId }: ExecuteDashboardAnalyticsQueryPathParameters,
  tLAnalyticsQueryRequest?: BodyType<TLAnalyticsQueryRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'dashboards',
    dashboardId,
    'analytics',
    'query',
    tLAnalyticsQueryRequest,
  ] as const;
};

export const getExecuteDashboardAnalyticsQueryQueryOptions = <
  TData = Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: ExecuteDashboardAnalyticsQueryPathParameters,
  tLAnalyticsQueryRequest: BodyType<TLAnalyticsQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getExecuteDashboardAnalyticsQueryQueryKey({ dashboardId }, tLAnalyticsQueryRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>> = ({ signal }) =>
    executeDashboardAnalyticsQuery({ dashboardId }, tLAnalyticsQueryRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ExecuteDashboardAnalyticsQueryQueryResult = NonNullable<
  Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>
>;
export type ExecuteDashboardAnalyticsQueryQueryError = ErrorType<TLHTTPValidationError>;

export function useExecuteDashboardAnalyticsQuery<
  TData = Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExecuteDashboardAnalyticsQueryPathParameters,
  tLAnalyticsQueryRequest: BodyType<TLAnalyticsQueryRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>,
          TError,
          Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExecuteDashboardAnalyticsQuery<
  TData = Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExecuteDashboardAnalyticsQueryPathParameters,
  tLAnalyticsQueryRequest: BodyType<TLAnalyticsQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>,
          TError,
          Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExecuteDashboardAnalyticsQuery<
  TData = Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExecuteDashboardAnalyticsQueryPathParameters,
  tLAnalyticsQueryRequest: BodyType<TLAnalyticsQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Execute Dashboard Analytics Query
 */

export function useExecuteDashboardAnalyticsQuery<
  TData = Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: ExecuteDashboardAnalyticsQueryPathParameters,
  tLAnalyticsQueryRequest: BodyType<TLAnalyticsQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExecuteDashboardAnalyticsQueryQueryOptions({ dashboardId }, tLAnalyticsQueryRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Execute Dashboard Analytics Query
 */
export const prefetchExecuteDashboardAnalyticsQueryQuery = async <
  TData = Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: ExecuteDashboardAnalyticsQueryPathParameters,
  tLAnalyticsQueryRequest: BodyType<TLAnalyticsQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeDashboardAnalyticsQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExecuteDashboardAnalyticsQueryQueryOptions({ dashboardId }, tLAnalyticsQueryRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Execute Dashboard Analytics Query
 */
export const invalidateExecuteDashboardAnalyticsQuery = async (
  queryClient: QueryClient,
  { dashboardId }: ExecuteDashboardAnalyticsQueryPathParameters,
  tLAnalyticsQueryRequest: BodyType<TLAnalyticsQueryRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getExecuteDashboardAnalyticsQueryQueryKey({ dashboardId }, tLAnalyticsQueryRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Execute Batch Dashboard Analytics Queries
 */
export const executeBatchDashboardAnalyticsQueries = (
  { dashboardId }: ExecuteBatchDashboardAnalyticsQueriesPathParameters,
  tLAnalyticsBatchQueryRequest: BodyType<TLAnalyticsBatchQueryRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAnalyticsBatchQueryResponse>(
    {
      url: `/v2/data-harmonization/dashboards/${dashboardId}/analytics/query/batch`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLAnalyticsBatchQueryRequest,
      signal,
    },
    options,
  );
};

export const getExecuteBatchDashboardAnalyticsQueriesQueryKey = (
  { dashboardId }: ExecuteBatchDashboardAnalyticsQueriesPathParameters,
  tLAnalyticsBatchQueryRequest?: BodyType<TLAnalyticsBatchQueryRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'dashboards',
    dashboardId,
    'analytics',
    'query',
    'batch',
    tLAnalyticsBatchQueryRequest,
  ] as const;
};

export const getExecuteBatchDashboardAnalyticsQueriesQueryOptions = <
  TData = Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: ExecuteBatchDashboardAnalyticsQueriesPathParameters,
  tLAnalyticsBatchQueryRequest: BodyType<TLAnalyticsBatchQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getExecuteBatchDashboardAnalyticsQueriesQueryKey({ dashboardId }, tLAnalyticsBatchQueryRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>> = ({ signal }) =>
    executeBatchDashboardAnalyticsQueries({ dashboardId }, tLAnalyticsBatchQueryRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ExecuteBatchDashboardAnalyticsQueriesQueryResult = NonNullable<
  Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>
>;
export type ExecuteBatchDashboardAnalyticsQueriesQueryError = ErrorType<TLHTTPValidationError>;

export function useExecuteBatchDashboardAnalyticsQueries<
  TData = Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExecuteBatchDashboardAnalyticsQueriesPathParameters,
  tLAnalyticsBatchQueryRequest: BodyType<TLAnalyticsBatchQueryRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>,
          TError,
          Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExecuteBatchDashboardAnalyticsQueries<
  TData = Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExecuteBatchDashboardAnalyticsQueriesPathParameters,
  tLAnalyticsBatchQueryRequest: BodyType<TLAnalyticsBatchQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>,
          TError,
          Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExecuteBatchDashboardAnalyticsQueries<
  TData = Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExecuteBatchDashboardAnalyticsQueriesPathParameters,
  tLAnalyticsBatchQueryRequest: BodyType<TLAnalyticsBatchQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Execute Batch Dashboard Analytics Queries
 */

export function useExecuteBatchDashboardAnalyticsQueries<
  TData = Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: ExecuteBatchDashboardAnalyticsQueriesPathParameters,
  tLAnalyticsBatchQueryRequest: BodyType<TLAnalyticsBatchQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExecuteBatchDashboardAnalyticsQueriesQueryOptions(
    { dashboardId },
    tLAnalyticsBatchQueryRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Execute Batch Dashboard Analytics Queries
 */
export const prefetchExecuteBatchDashboardAnalyticsQueriesQuery = async <
  TData = Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: ExecuteBatchDashboardAnalyticsQueriesPathParameters,
  tLAnalyticsBatchQueryRequest: BodyType<TLAnalyticsBatchQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeBatchDashboardAnalyticsQueries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExecuteBatchDashboardAnalyticsQueriesQueryOptions(
    { dashboardId },
    tLAnalyticsBatchQueryRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Execute Batch Dashboard Analytics Queries
 */
export const invalidateExecuteBatchDashboardAnalyticsQueries = async (
  queryClient: QueryClient,
  { dashboardId }: ExecuteBatchDashboardAnalyticsQueriesPathParameters,
  tLAnalyticsBatchQueryRequest: BodyType<TLAnalyticsBatchQueryRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getExecuteBatchDashboardAnalyticsQueriesQueryKey({ dashboardId }, tLAnalyticsBatchQueryRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Dashboard Config
 */
export const updateDashboardConfig = (
  { dashboardId }: UpdateDashboardConfigPathParameters,
  tLDashboardConfigUpdateRequest: BodyType<TLDashboardConfigUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardConfigResponse>(
    {
      url: `/v2/data-harmonization/dashboards/${dashboardId}/config`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLDashboardConfigUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateDashboardConfigQueryKey = (
  { dashboardId }: UpdateDashboardConfigPathParameters,
  tLDashboardConfigUpdateRequest?: BodyType<TLDashboardConfigUpdateRequest>,
) => {
  return [
    'PATCH',
    'v2',
    'data-harmonization',
    'dashboards',
    dashboardId,
    'config',
    tLDashboardConfigUpdateRequest,
  ] as const;
};

export const getUpdateDashboardConfigQueryOptions = <
  TData = Awaited<ReturnType<typeof updateDashboardConfig>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: UpdateDashboardConfigPathParameters,
  tLDashboardConfigUpdateRequest: BodyType<TLDashboardConfigUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardConfig>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getUpdateDashboardConfigQueryKey({ dashboardId }, tLDashboardConfigUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateDashboardConfig>>> = ({ signal }) =>
    updateDashboardConfig({ dashboardId }, tLDashboardConfigUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateDashboardConfig>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateDashboardConfigQueryResult = NonNullable<Awaited<ReturnType<typeof updateDashboardConfig>>>;
export type UpdateDashboardConfigQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateDashboardConfig<
  TData = Awaited<ReturnType<typeof updateDashboardConfig>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDashboardConfigPathParameters,
  tLDashboardConfigUpdateRequest: BodyType<TLDashboardConfigUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardConfig>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDashboardConfig>>,
          TError,
          Awaited<ReturnType<typeof updateDashboardConfig>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDashboardConfig<
  TData = Awaited<ReturnType<typeof updateDashboardConfig>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDashboardConfigPathParameters,
  tLDashboardConfigUpdateRequest: BodyType<TLDashboardConfigUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardConfig>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDashboardConfig>>,
          TError,
          Awaited<ReturnType<typeof updateDashboardConfig>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDashboardConfig<
  TData = Awaited<ReturnType<typeof updateDashboardConfig>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDashboardConfigPathParameters,
  tLDashboardConfigUpdateRequest: BodyType<TLDashboardConfigUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardConfig>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Dashboard Config
 */

export function useUpdateDashboardConfig<
  TData = Awaited<ReturnType<typeof updateDashboardConfig>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: UpdateDashboardConfigPathParameters,
  tLDashboardConfigUpdateRequest: BodyType<TLDashboardConfigUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardConfig>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateDashboardConfigQueryOptions({ dashboardId }, tLDashboardConfigUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Dashboard Config
 */
export const prefetchUpdateDashboardConfigQuery = async <
  TData = Awaited<ReturnType<typeof updateDashboardConfig>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: UpdateDashboardConfigPathParameters,
  tLDashboardConfigUpdateRequest: BodyType<TLDashboardConfigUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardConfig>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateDashboardConfigQueryOptions({ dashboardId }, tLDashboardConfigUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Dashboard Config
 */
export const invalidateUpdateDashboardConfig = async (
  queryClient: QueryClient,
  { dashboardId }: UpdateDashboardConfigPathParameters,
  tLDashboardConfigUpdateRequest: BodyType<TLDashboardConfigUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateDashboardConfigQueryKey({ dashboardId }, tLDashboardConfigUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Remove Credentials From Dashboard
 */
export const removeCredentialsFromDashboard = (
  { dashboardId }: RemoveCredentialsFromDashboardPathParameters,
  tLRemoveCredentialsRequest: BodyType<TLRemoveCredentialsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    {
      url: `/v2/data-harmonization/dashboards/${dashboardId}/credentials`,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      data: tLRemoveCredentialsRequest,
      signal,
    },
    options,
  );
};

export const getRemoveCredentialsFromDashboardQueryKey = (
  { dashboardId }: RemoveCredentialsFromDashboardPathParameters,
  tLRemoveCredentialsRequest?: BodyType<TLRemoveCredentialsRequest>,
) => {
  return [
    'DELETE',
    'v2',
    'data-harmonization',
    'dashboards',
    dashboardId,
    'credentials',
    tLRemoveCredentialsRequest,
  ] as const;
};

export const getRemoveCredentialsFromDashboardQueryOptions = <
  TData = Awaited<ReturnType<typeof removeCredentialsFromDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: RemoveCredentialsFromDashboardPathParameters,
  tLRemoveCredentialsRequest: BodyType<TLRemoveCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeCredentialsFromDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getRemoveCredentialsFromDashboardQueryKey({ dashboardId }, tLRemoveCredentialsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof removeCredentialsFromDashboard>>> = ({ signal }) =>
    removeCredentialsFromDashboard({ dashboardId }, tLRemoveCredentialsRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof removeCredentialsFromDashboard>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RemoveCredentialsFromDashboardQueryResult = NonNullable<
  Awaited<ReturnType<typeof removeCredentialsFromDashboard>>
>;
export type RemoveCredentialsFromDashboardQueryError = ErrorType<TLHTTPValidationError>;

export function useRemoveCredentialsFromDashboard<
  TData = Awaited<ReturnType<typeof removeCredentialsFromDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveCredentialsFromDashboardPathParameters,
  tLRemoveCredentialsRequest: BodyType<TLRemoveCredentialsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeCredentialsFromDashboard>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeCredentialsFromDashboard>>,
          TError,
          Awaited<ReturnType<typeof removeCredentialsFromDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveCredentialsFromDashboard<
  TData = Awaited<ReturnType<typeof removeCredentialsFromDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveCredentialsFromDashboardPathParameters,
  tLRemoveCredentialsRequest: BodyType<TLRemoveCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeCredentialsFromDashboard>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeCredentialsFromDashboard>>,
          TError,
          Awaited<ReturnType<typeof removeCredentialsFromDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveCredentialsFromDashboard<
  TData = Awaited<ReturnType<typeof removeCredentialsFromDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveCredentialsFromDashboardPathParameters,
  tLRemoveCredentialsRequest: BodyType<TLRemoveCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeCredentialsFromDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Remove Credentials From Dashboard
 */

export function useRemoveCredentialsFromDashboard<
  TData = Awaited<ReturnType<typeof removeCredentialsFromDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: RemoveCredentialsFromDashboardPathParameters,
  tLRemoveCredentialsRequest: BodyType<TLRemoveCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeCredentialsFromDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRemoveCredentialsFromDashboardQueryOptions(
    { dashboardId },
    tLRemoveCredentialsRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Remove Credentials From Dashboard
 */
export const prefetchRemoveCredentialsFromDashboardQuery = async <
  TData = Awaited<ReturnType<typeof removeCredentialsFromDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: RemoveCredentialsFromDashboardPathParameters,
  tLRemoveCredentialsRequest: BodyType<TLRemoveCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeCredentialsFromDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRemoveCredentialsFromDashboardQueryOptions(
    { dashboardId },
    tLRemoveCredentialsRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Remove Credentials From Dashboard
 */
export const invalidateRemoveCredentialsFromDashboard = async (
  queryClient: QueryClient,
  { dashboardId }: RemoveCredentialsFromDashboardPathParameters,
  tLRemoveCredentialsRequest: BodyType<TLRemoveCredentialsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRemoveCredentialsFromDashboardQueryKey({ dashboardId }, tLRemoveCredentialsRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Dashboard Credentials
 */
export const getDashboardCredentials = (
  { dashboardId }: GetDashboardCredentialsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardCredentialMappingsResponse>(
    { url: `/v2/data-harmonization/dashboards/${dashboardId}/credentials`, method: 'GET', signal },
    options,
  );
};

export const getGetDashboardCredentialsQueryKey = ({ dashboardId }: GetDashboardCredentialsPathParameters) => {
  return ['v2', 'data-harmonization', 'dashboards', dashboardId, 'credentials'] as const;
};

export const getGetDashboardCredentialsQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboardCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardCredentialsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardCredentialsQueryKey({ dashboardId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardCredentials>>> = ({ signal }) =>
    getDashboardCredentials({ dashboardId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDashboardCredentials>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDashboardCredentialsQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardCredentials>>>;
export type GetDashboardCredentialsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDashboardCredentials<
  TData = Awaited<ReturnType<typeof getDashboardCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardCredentialsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardCredentials>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardCredentials>>,
          TError,
          Awaited<ReturnType<typeof getDashboardCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardCredentials<
  TData = Awaited<ReturnType<typeof getDashboardCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardCredentialsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardCredentials>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardCredentials>>,
          TError,
          Awaited<ReturnType<typeof getDashboardCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardCredentials<
  TData = Awaited<ReturnType<typeof getDashboardCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardCredentialsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Dashboard Credentials
 */

export function useGetDashboardCredentials<
  TData = Awaited<ReturnType<typeof getDashboardCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardCredentialsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDashboardCredentialsQueryOptions({ dashboardId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Dashboard Credentials
 */
export const prefetchGetDashboardCredentialsQuery = async <
  TData = Awaited<ReturnType<typeof getDashboardCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: GetDashboardCredentialsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDashboardCredentialsQueryOptions({ dashboardId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Dashboard Credentials
 */
export const invalidateGetDashboardCredentials = async (
  queryClient: QueryClient,
  { dashboardId }: GetDashboardCredentialsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetDashboardCredentialsQueryKey({ dashboardId }) }, options);

  return queryClient;
};

/**
 * @summary Add Credentials To Dashboard
 */
export const addCredentialsToDashboard = (
  { dashboardId }: AddCredentialsToDashboardPathParameters,
  tLAddCredentialsRequest: BodyType<TLAddCredentialsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardCredentialMappingsResponse>(
    {
      url: `/v2/data-harmonization/dashboards/${dashboardId}/credentials`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLAddCredentialsRequest,
      signal,
    },
    options,
  );
};

export const getAddCredentialsToDashboardQueryKey = (
  { dashboardId }: AddCredentialsToDashboardPathParameters,
  tLAddCredentialsRequest?: BodyType<TLAddCredentialsRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'dashboards',
    dashboardId,
    'credentials',
    tLAddCredentialsRequest,
  ] as const;
};

export const getAddCredentialsToDashboardQueryOptions = <
  TData = Awaited<ReturnType<typeof addCredentialsToDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: AddCredentialsToDashboardPathParameters,
  tLAddCredentialsRequest: BodyType<TLAddCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addCredentialsToDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAddCredentialsToDashboardQueryKey({ dashboardId }, tLAddCredentialsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof addCredentialsToDashboard>>> = ({ signal }) =>
    addCredentialsToDashboard({ dashboardId }, tLAddCredentialsRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof addCredentialsToDashboard>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AddCredentialsToDashboardQueryResult = NonNullable<Awaited<ReturnType<typeof addCredentialsToDashboard>>>;
export type AddCredentialsToDashboardQueryError = ErrorType<TLHTTPValidationError>;

export function useAddCredentialsToDashboard<
  TData = Awaited<ReturnType<typeof addCredentialsToDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddCredentialsToDashboardPathParameters,
  tLAddCredentialsRequest: BodyType<TLAddCredentialsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof addCredentialsToDashboard>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof addCredentialsToDashboard>>,
          TError,
          Awaited<ReturnType<typeof addCredentialsToDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddCredentialsToDashboard<
  TData = Awaited<ReturnType<typeof addCredentialsToDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddCredentialsToDashboardPathParameters,
  tLAddCredentialsRequest: BodyType<TLAddCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addCredentialsToDashboard>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof addCredentialsToDashboard>>,
          TError,
          Awaited<ReturnType<typeof addCredentialsToDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddCredentialsToDashboard<
  TData = Awaited<ReturnType<typeof addCredentialsToDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddCredentialsToDashboardPathParameters,
  tLAddCredentialsRequest: BodyType<TLAddCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addCredentialsToDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Add Credentials To Dashboard
 */

export function useAddCredentialsToDashboard<
  TData = Awaited<ReturnType<typeof addCredentialsToDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: AddCredentialsToDashboardPathParameters,
  tLAddCredentialsRequest: BodyType<TLAddCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addCredentialsToDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAddCredentialsToDashboardQueryOptions({ dashboardId }, tLAddCredentialsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add Credentials To Dashboard
 */
export const prefetchAddCredentialsToDashboardQuery = async <
  TData = Awaited<ReturnType<typeof addCredentialsToDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: AddCredentialsToDashboardPathParameters,
  tLAddCredentialsRequest: BodyType<TLAddCredentialsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addCredentialsToDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAddCredentialsToDashboardQueryOptions({ dashboardId }, tLAddCredentialsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Add Credentials To Dashboard
 */
export const invalidateAddCredentialsToDashboard = async (
  queryClient: QueryClient,
  { dashboardId }: AddCredentialsToDashboardPathParameters,
  tLAddCredentialsRequest: BodyType<TLAddCredentialsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAddCredentialsToDashboardQueryKey({ dashboardId }, tLAddCredentialsRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Dashboard Data Check Records
 */
export const getDashboardDataCheckRecords = (
  { dashboardId }: GetDashboardDataCheckRecordsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGetDashboardDataChecksResponse>(
    { url: `/v2/data-harmonization/dashboards/${dashboardId}/dashboard-data-checks`, method: 'GET', signal },
    options,
  );
};

export const getGetDashboardDataCheckRecordsQueryKey = ({
  dashboardId,
}: GetDashboardDataCheckRecordsPathParameters) => {
  return ['v2', 'data-harmonization', 'dashboards', dashboardId, 'dashboard-data-checks'] as const;
};

export const getGetDashboardDataCheckRecordsQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboardDataCheckRecords>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardDataCheckRecordsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataCheckRecords>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardDataCheckRecordsQueryKey({ dashboardId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardDataCheckRecords>>> = ({ signal }) =>
    getDashboardDataCheckRecords({ dashboardId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataCheckRecords>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDashboardDataCheckRecordsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDashboardDataCheckRecords>>
>;
export type GetDashboardDataCheckRecordsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDashboardDataCheckRecords<
  TData = Awaited<ReturnType<typeof getDashboardDataCheckRecords>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardDataCheckRecordsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataCheckRecords>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardDataCheckRecords>>,
          TError,
          Awaited<ReturnType<typeof getDashboardDataCheckRecords>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardDataCheckRecords<
  TData = Awaited<ReturnType<typeof getDashboardDataCheckRecords>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardDataCheckRecordsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataCheckRecords>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardDataCheckRecords>>,
          TError,
          Awaited<ReturnType<typeof getDashboardDataCheckRecords>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardDataCheckRecords<
  TData = Awaited<ReturnType<typeof getDashboardDataCheckRecords>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardDataCheckRecordsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataCheckRecords>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Dashboard Data Check Records
 */

export function useGetDashboardDataCheckRecords<
  TData = Awaited<ReturnType<typeof getDashboardDataCheckRecords>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardDataCheckRecordsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataCheckRecords>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDashboardDataCheckRecordsQueryOptions({ dashboardId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Dashboard Data Check Records
 */
export const prefetchGetDashboardDataCheckRecordsQuery = async <
  TData = Awaited<ReturnType<typeof getDashboardDataCheckRecords>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: GetDashboardDataCheckRecordsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataCheckRecords>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDashboardDataCheckRecordsQueryOptions({ dashboardId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Dashboard Data Check Records
 */
export const invalidateGetDashboardDataCheckRecords = async (
  queryClient: QueryClient,
  { dashboardId }: GetDashboardDataCheckRecordsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetDashboardDataCheckRecordsQueryKey({ dashboardId }) }, options);

  return queryClient;
};

/**
 * @summary Get Dashboard Data Checks
 */
export const getDashboardDataChecks = (
  { dashboardId }: GetDashboardDataChecksPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataChecksResponse>(
    { url: `/v2/data-harmonization/dashboards/${dashboardId}/data-checks`, method: 'GET', signal },
    options,
  );
};

export const getGetDashboardDataChecksQueryKey = ({ dashboardId }: GetDashboardDataChecksPathParameters) => {
  return ['v2', 'data-harmonization', 'dashboards', dashboardId, 'data-checks'] as const;
};

export const getGetDashboardDataChecksQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboardDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardDataChecksPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardDataChecksQueryKey({ dashboardId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardDataChecks>>> = ({ signal }) =>
    getDashboardDataChecks({ dashboardId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataChecks>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDashboardDataChecksQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardDataChecks>>>;
export type GetDashboardDataChecksQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDashboardDataChecks<
  TData = Awaited<ReturnType<typeof getDashboardDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardDataChecksPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataChecks>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardDataChecks>>,
          TError,
          Awaited<ReturnType<typeof getDashboardDataChecks>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardDataChecks<
  TData = Awaited<ReturnType<typeof getDashboardDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardDataChecksPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataChecks>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardDataChecks>>,
          TError,
          Awaited<ReturnType<typeof getDashboardDataChecks>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardDataChecks<
  TData = Awaited<ReturnType<typeof getDashboardDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardDataChecksPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Dashboard Data Checks
 */

export function useGetDashboardDataChecks<
  TData = Awaited<ReturnType<typeof getDashboardDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardDataChecksPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDashboardDataChecksQueryOptions({ dashboardId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Dashboard Data Checks
 */
export const prefetchGetDashboardDataChecksQuery = async <
  TData = Awaited<ReturnType<typeof getDashboardDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: GetDashboardDataChecksPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDashboardDataChecksQueryOptions({ dashboardId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Dashboard Data Checks
 */
export const invalidateGetDashboardDataChecks = async (
  queryClient: QueryClient,
  { dashboardId }: GetDashboardDataChecksPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetDashboardDataChecksQueryKey({ dashboardId }) }, options);

  return queryClient;
};

/**
 * @summary Add Data Checks To Dashboard
 */
export const addDataChecksToDashboard = (
  { dashboardId }: AddDataChecksToDashboardPathParameters,
  tLDashboardDataCheckAddRequest: BodyType<TLDashboardDataCheckAddRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardDataChecksResult>(
    {
      url: `/v2/data-harmonization/dashboards/${dashboardId}/data-checks`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLDashboardDataCheckAddRequest,
      signal,
    },
    options,
  );
};

export const getAddDataChecksToDashboardQueryKey = (
  { dashboardId }: AddDataChecksToDashboardPathParameters,
  tLDashboardDataCheckAddRequest?: BodyType<TLDashboardDataCheckAddRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'dashboards',
    dashboardId,
    'data-checks',
    tLDashboardDataCheckAddRequest,
  ] as const;
};

export const getAddDataChecksToDashboardQueryOptions = <
  TData = Awaited<ReturnType<typeof addDataChecksToDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: AddDataChecksToDashboardPathParameters,
  tLDashboardDataCheckAddRequest: BodyType<TLDashboardDataCheckAddRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAddDataChecksToDashboardQueryKey({ dashboardId }, tLDashboardDataCheckAddRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof addDataChecksToDashboard>>> = ({ signal }) =>
    addDataChecksToDashboard({ dashboardId }, tLDashboardDataCheckAddRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToDashboard>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AddDataChecksToDashboardQueryResult = NonNullable<Awaited<ReturnType<typeof addDataChecksToDashboard>>>;
export type AddDataChecksToDashboardQueryError = ErrorType<TLHTTPValidationError>;

export function useAddDataChecksToDashboard<
  TData = Awaited<ReturnType<typeof addDataChecksToDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddDataChecksToDashboardPathParameters,
  tLDashboardDataCheckAddRequest: BodyType<TLDashboardDataCheckAddRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToDashboard>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof addDataChecksToDashboard>>,
          TError,
          Awaited<ReturnType<typeof addDataChecksToDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddDataChecksToDashboard<
  TData = Awaited<ReturnType<typeof addDataChecksToDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddDataChecksToDashboardPathParameters,
  tLDashboardDataCheckAddRequest: BodyType<TLDashboardDataCheckAddRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToDashboard>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof addDataChecksToDashboard>>,
          TError,
          Awaited<ReturnType<typeof addDataChecksToDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddDataChecksToDashboard<
  TData = Awaited<ReturnType<typeof addDataChecksToDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddDataChecksToDashboardPathParameters,
  tLDashboardDataCheckAddRequest: BodyType<TLDashboardDataCheckAddRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Add Data Checks To Dashboard
 */

export function useAddDataChecksToDashboard<
  TData = Awaited<ReturnType<typeof addDataChecksToDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: AddDataChecksToDashboardPathParameters,
  tLDashboardDataCheckAddRequest: BodyType<TLDashboardDataCheckAddRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAddDataChecksToDashboardQueryOptions(
    { dashboardId },
    tLDashboardDataCheckAddRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add Data Checks To Dashboard
 */
export const prefetchAddDataChecksToDashboardQuery = async <
  TData = Awaited<ReturnType<typeof addDataChecksToDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: AddDataChecksToDashboardPathParameters,
  tLDashboardDataCheckAddRequest: BodyType<TLDashboardDataCheckAddRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAddDataChecksToDashboardQueryOptions(
    { dashboardId },
    tLDashboardDataCheckAddRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Add Data Checks To Dashboard
 */
export const invalidateAddDataChecksToDashboard = async (
  queryClient: QueryClient,
  { dashboardId }: AddDataChecksToDashboardPathParameters,
  tLDashboardDataCheckAddRequest: BodyType<TLDashboardDataCheckAddRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAddDataChecksToDashboardQueryKey({ dashboardId }, tLDashboardDataCheckAddRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Materialize Dashboard Data Source Query
 */
export const materializeDashboardDataSourceQuery = (
  { dashboardId }: MaterializeDashboardDataSourceQueryPathParameters,
  tLDataSourceQueryRequest: BodyType<TLDataSourceQueryRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataSourceQueryResponse>(
    {
      url: `/v2/data-harmonization/dashboards/${dashboardId}/data-sources/from-query`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLDataSourceQueryRequest,
      signal,
    },
    options,
  );
};

export const getMaterializeDashboardDataSourceQueryQueryKey = (
  { dashboardId }: MaterializeDashboardDataSourceQueryPathParameters,
  tLDataSourceQueryRequest?: BodyType<TLDataSourceQueryRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'dashboards',
    dashboardId,
    'data-sources',
    'from-query',
    tLDataSourceQueryRequest,
  ] as const;
};

export const getMaterializeDashboardDataSourceQueryQueryOptions = <
  TData = Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: MaterializeDashboardDataSourceQueryPathParameters,
  tLDataSourceQueryRequest: BodyType<TLDataSourceQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getMaterializeDashboardDataSourceQueryQueryKey({ dashboardId }, tLDataSourceQueryRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>> = ({ signal }) =>
    materializeDashboardDataSourceQuery({ dashboardId }, tLDataSourceQueryRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type MaterializeDashboardDataSourceQueryQueryResult = NonNullable<
  Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>
>;
export type MaterializeDashboardDataSourceQueryQueryError = ErrorType<TLHTTPValidationError>;

export function useMaterializeDashboardDataSourceQuery<
  TData = Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MaterializeDashboardDataSourceQueryPathParameters,
  tLDataSourceQueryRequest: BodyType<TLDataSourceQueryRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>,
          TError,
          Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMaterializeDashboardDataSourceQuery<
  TData = Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MaterializeDashboardDataSourceQueryPathParameters,
  tLDataSourceQueryRequest: BodyType<TLDataSourceQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>,
          TError,
          Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMaterializeDashboardDataSourceQuery<
  TData = Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MaterializeDashboardDataSourceQueryPathParameters,
  tLDataSourceQueryRequest: BodyType<TLDataSourceQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Materialize Dashboard Data Source Query
 */

export function useMaterializeDashboardDataSourceQuery<
  TData = Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: MaterializeDashboardDataSourceQueryPathParameters,
  tLDataSourceQueryRequest: BodyType<TLDataSourceQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMaterializeDashboardDataSourceQueryQueryOptions(
    { dashboardId },
    tLDataSourceQueryRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Materialize Dashboard Data Source Query
 */
export const prefetchMaterializeDashboardDataSourceQueryQuery = async <
  TData = Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: MaterializeDashboardDataSourceQueryPathParameters,
  tLDataSourceQueryRequest: BodyType<TLDataSourceQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof materializeDashboardDataSourceQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMaterializeDashboardDataSourceQueryQueryOptions(
    { dashboardId },
    tLDataSourceQueryRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Materialize Dashboard Data Source Query
 */
export const invalidateMaterializeDashboardDataSourceQuery = async (
  queryClient: QueryClient,
  { dashboardId }: MaterializeDashboardDataSourceQueryPathParameters,
  tLDataSourceQueryRequest: BodyType<TLDataSourceQueryRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMaterializeDashboardDataSourceQueryQueryKey({ dashboardId }, tLDataSourceQueryRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Dashboard Data Source Query Status
 */
export const getDashboardDataSourceQueryStatus = (
  { dashboardId, jobId }: GetDashboardDataSourceQueryStatusPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLQueryStatusResponse>(
    { url: `/v2/data-harmonization/dashboards/${dashboardId}/data-sources/from-query/${jobId}`, method: 'GET', signal },
    options,
  );
};

export const getGetDashboardDataSourceQueryStatusQueryKey = ({
  dashboardId,
  jobId,
}: GetDashboardDataSourceQueryStatusPathParameters) => {
  return ['v2', 'data-harmonization', 'dashboards', dashboardId, 'data-sources', 'from-query', jobId] as const;
};

export const getGetDashboardDataSourceQueryStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId, jobId }: GetDashboardDataSourceQueryStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardDataSourceQueryStatusQueryKey({ dashboardId, jobId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>> = ({ signal }) =>
    getDashboardDataSourceQueryStatus({ dashboardId, jobId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined && jobId !== null && jobId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDashboardDataSourceQueryStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>
>;
export type GetDashboardDataSourceQueryStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDashboardDataSourceQueryStatus<
  TData = Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardDataSourceQueryStatusPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>,
          TError,
          Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardDataSourceQueryStatus<
  TData = Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardDataSourceQueryStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>,
          TError,
          Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardDataSourceQueryStatus<
  TData = Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardDataSourceQueryStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Dashboard Data Source Query Status
 */

export function useGetDashboardDataSourceQueryStatus<
  TData = Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId, jobId }: GetDashboardDataSourceQueryStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDashboardDataSourceQueryStatusQueryOptions({ dashboardId, jobId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Dashboard Data Source Query Status
 */
export const prefetchGetDashboardDataSourceQueryStatusQuery = async <
  TData = Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId, jobId }: GetDashboardDataSourceQueryStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceQueryStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDashboardDataSourceQueryStatusQueryOptions({ dashboardId, jobId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Dashboard Data Source Query Status
 */
export const invalidateGetDashboardDataSourceQueryStatus = async (
  queryClient: QueryClient,
  { dashboardId, jobId }: GetDashboardDataSourceQueryStatusPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetDashboardDataSourceQueryStatusQueryKey({ dashboardId, jobId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Import Dashboard Data Source File
 */
export const importDashboardDataSourceFile = (
  { dashboardId }: ImportDashboardDataSourceFilePathParameters,
  tLImportDashboardDataSourceFileHarmonizationRequest: BodyType<TLImportDashboardDataSourceFileHarmonizationRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  if (
    tLImportDashboardDataSourceFileHarmonizationRequest.catalog !== undefined &&
    tLImportDashboardDataSourceFileHarmonizationRequest.catalog !== null
  ) {
    formData.append(`catalog`, tLImportDashboardDataSourceFileHarmonizationRequest.catalog);
  }
  formData.append(`file`, tLImportDashboardDataSourceFileHarmonizationRequest.file);
  if (
    tLImportDashboardDataSourceFileHarmonizationRequest.schema_name !== undefined &&
    tLImportDashboardDataSourceFileHarmonizationRequest.schema_name !== null
  ) {
    formData.append(`schema_name`, tLImportDashboardDataSourceFileHarmonizationRequest.schema_name);
  }
  if (
    tLImportDashboardDataSourceFileHarmonizationRequest.table_name !== undefined &&
    tLImportDashboardDataSourceFileHarmonizationRequest.table_name !== null
  ) {
    formData.append(`table_name`, tLImportDashboardDataSourceFileHarmonizationRequest.table_name);
  }

  return apiInstance<TLDataSourceImportFileResponse>(
    {
      url: `/v2/data-harmonization/dashboards/${dashboardId}/data-sources/import-file`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      signal,
    },
    options,
  );
};

export const getImportDashboardDataSourceFileQueryKey = (
  { dashboardId }: ImportDashboardDataSourceFilePathParameters,
  tLImportDashboardDataSourceFileHarmonizationRequest?: BodyType<TLImportDashboardDataSourceFileHarmonizationRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'dashboards',
    dashboardId,
    'data-sources',
    'import-file',
    tLImportDashboardDataSourceFileHarmonizationRequest,
  ] as const;
};

export const getImportDashboardDataSourceFileQueryOptions = <
  TData = Awaited<ReturnType<typeof importDashboardDataSourceFile>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: ImportDashboardDataSourceFilePathParameters,
  tLImportDashboardDataSourceFileHarmonizationRequest: BodyType<TLImportDashboardDataSourceFileHarmonizationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importDashboardDataSourceFile>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getImportDashboardDataSourceFileQueryKey({ dashboardId }, tLImportDashboardDataSourceFileHarmonizationRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof importDashboardDataSourceFile>>> = ({ signal }) =>
    importDashboardDataSourceFile(
      { dashboardId },
      tLImportDashboardDataSourceFileHarmonizationRequest,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof importDashboardDataSourceFile>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ImportDashboardDataSourceFileQueryResult = NonNullable<
  Awaited<ReturnType<typeof importDashboardDataSourceFile>>
>;
export type ImportDashboardDataSourceFileQueryError = ErrorType<TLHTTPValidationError>;

export function useImportDashboardDataSourceFile<
  TData = Awaited<ReturnType<typeof importDashboardDataSourceFile>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ImportDashboardDataSourceFilePathParameters,
  tLImportDashboardDataSourceFileHarmonizationRequest: BodyType<TLImportDashboardDataSourceFileHarmonizationRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof importDashboardDataSourceFile>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof importDashboardDataSourceFile>>,
          TError,
          Awaited<ReturnType<typeof importDashboardDataSourceFile>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useImportDashboardDataSourceFile<
  TData = Awaited<ReturnType<typeof importDashboardDataSourceFile>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ImportDashboardDataSourceFilePathParameters,
  tLImportDashboardDataSourceFileHarmonizationRequest: BodyType<TLImportDashboardDataSourceFileHarmonizationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importDashboardDataSourceFile>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof importDashboardDataSourceFile>>,
          TError,
          Awaited<ReturnType<typeof importDashboardDataSourceFile>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useImportDashboardDataSourceFile<
  TData = Awaited<ReturnType<typeof importDashboardDataSourceFile>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ImportDashboardDataSourceFilePathParameters,
  tLImportDashboardDataSourceFileHarmonizationRequest: BodyType<TLImportDashboardDataSourceFileHarmonizationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importDashboardDataSourceFile>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Import Dashboard Data Source File
 */

export function useImportDashboardDataSourceFile<
  TData = Awaited<ReturnType<typeof importDashboardDataSourceFile>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: ImportDashboardDataSourceFilePathParameters,
  tLImportDashboardDataSourceFileHarmonizationRequest: BodyType<TLImportDashboardDataSourceFileHarmonizationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importDashboardDataSourceFile>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getImportDashboardDataSourceFileQueryOptions(
    { dashboardId },
    tLImportDashboardDataSourceFileHarmonizationRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Import Dashboard Data Source File
 */
export const prefetchImportDashboardDataSourceFileQuery = async <
  TData = Awaited<ReturnType<typeof importDashboardDataSourceFile>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: ImportDashboardDataSourceFilePathParameters,
  tLImportDashboardDataSourceFileHarmonizationRequest: BodyType<TLImportDashboardDataSourceFileHarmonizationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importDashboardDataSourceFile>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getImportDashboardDataSourceFileQueryOptions(
    { dashboardId },
    tLImportDashboardDataSourceFileHarmonizationRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Import Dashboard Data Source File
 */
export const invalidateImportDashboardDataSourceFile = async (
  queryClient: QueryClient,
  { dashboardId }: ImportDashboardDataSourceFilePathParameters,
  tLImportDashboardDataSourceFileHarmonizationRequest: BodyType<TLImportDashboardDataSourceFileHarmonizationRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    {
      queryKey: getImportDashboardDataSourceFileQueryKey(
        { dashboardId },
        tLImportDashboardDataSourceFileHarmonizationRequest,
      ),
    },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Dashboard Data Source Import Status
 */
export const getDashboardDataSourceImportStatus = (
  { dashboardId, jobId }: GetDashboardDataSourceImportStatusPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFoundryTableImportResponse>(
    {
      url: `/v2/data-harmonization/dashboards/${dashboardId}/data-sources/import-file/${jobId}`,
      method: 'GET',
      signal,
    },
    options,
  );
};

export const getGetDashboardDataSourceImportStatusQueryKey = ({
  dashboardId,
  jobId,
}: GetDashboardDataSourceImportStatusPathParameters) => {
  return ['v2', 'data-harmonization', 'dashboards', dashboardId, 'data-sources', 'import-file', jobId] as const;
};

export const getGetDashboardDataSourceImportStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId, jobId }: GetDashboardDataSourceImportStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardDataSourceImportStatusQueryKey({ dashboardId, jobId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>> = ({ signal }) =>
    getDashboardDataSourceImportStatus({ dashboardId, jobId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined && jobId !== null && jobId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDashboardDataSourceImportStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>
>;
export type GetDashboardDataSourceImportStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDashboardDataSourceImportStatus<
  TData = Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardDataSourceImportStatusPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>,
          TError,
          Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardDataSourceImportStatus<
  TData = Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardDataSourceImportStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>,
          TError,
          Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardDataSourceImportStatus<
  TData = Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardDataSourceImportStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Dashboard Data Source Import Status
 */

export function useGetDashboardDataSourceImportStatus<
  TData = Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId, jobId }: GetDashboardDataSourceImportStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDashboardDataSourceImportStatusQueryOptions({ dashboardId, jobId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Dashboard Data Source Import Status
 */
export const prefetchGetDashboardDataSourceImportStatusQuery = async <
  TData = Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId, jobId }: GetDashboardDataSourceImportStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardDataSourceImportStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDashboardDataSourceImportStatusQueryOptions({ dashboardId, jobId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Dashboard Data Source Import Status
 */
export const invalidateGetDashboardDataSourceImportStatus = async (
  queryClient: QueryClient,
  { dashboardId, jobId }: GetDashboardDataSourceImportStatusPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetDashboardDataSourceImportStatusQueryKey({ dashboardId, jobId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get All Error Tracking
 */
export const getAllErrorTracking = (
  { dashboardId }: GetAllErrorTrackingPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGetAllErrorTrackingResponse>(
    { url: `/v2/data-harmonization/dashboards/${dashboardId}/error-tracking`, method: 'GET', signal },
    options,
  );
};

export const getGetAllErrorTrackingQueryKey = ({ dashboardId }: GetAllErrorTrackingPathParameters) => {
  return ['v2', 'data-harmonization', 'dashboards', dashboardId, 'error-tracking'] as const;
};

export const getGetAllErrorTrackingQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllErrorTracking>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetAllErrorTrackingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllErrorTracking>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllErrorTrackingQueryKey({ dashboardId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllErrorTracking>>> = ({ signal }) =>
    getAllErrorTracking({ dashboardId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getAllErrorTracking>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetAllErrorTrackingQueryResult = NonNullable<Awaited<ReturnType<typeof getAllErrorTracking>>>;
export type GetAllErrorTrackingQueryError = ErrorType<TLHTTPValidationError>;

export function useGetAllErrorTracking<
  TData = Awaited<ReturnType<typeof getAllErrorTracking>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAllErrorTrackingPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllErrorTracking>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllErrorTracking>>,
          TError,
          Awaited<ReturnType<typeof getAllErrorTracking>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllErrorTracking<
  TData = Awaited<ReturnType<typeof getAllErrorTracking>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAllErrorTrackingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllErrorTracking>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllErrorTracking>>,
          TError,
          Awaited<ReturnType<typeof getAllErrorTracking>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllErrorTracking<
  TData = Awaited<ReturnType<typeof getAllErrorTracking>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAllErrorTrackingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllErrorTracking>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Error Tracking
 */

export function useGetAllErrorTracking<
  TData = Awaited<ReturnType<typeof getAllErrorTracking>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetAllErrorTrackingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllErrorTracking>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllErrorTrackingQueryOptions({ dashboardId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Error Tracking
 */
export const prefetchGetAllErrorTrackingQuery = async <
  TData = Awaited<ReturnType<typeof getAllErrorTracking>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: GetAllErrorTrackingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllErrorTracking>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllErrorTrackingQueryOptions({ dashboardId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Error Tracking
 */
export const invalidateGetAllErrorTracking = async (
  queryClient: QueryClient,
  { dashboardId }: GetAllErrorTrackingPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAllErrorTrackingQueryKey({ dashboardId }) }, options);

  return queryClient;
};

/**
 * @summary Execute Dashboard
 */
export const executeDashboard = (
  { dashboardId }: ExecuteDashboardPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLExecuteDashboardResponse>(
    { url: `/v2/data-harmonization/dashboards/${dashboardId}/execute`, method: 'POST', signal },
    options,
  );
};

export const getExecuteDashboardQueryKey = ({ dashboardId }: ExecuteDashboardPathParameters) => {
  return ['POST', 'v2', 'data-harmonization', 'dashboards', dashboardId, 'execute'] as const;
};

export const getExecuteDashboardQueryOptions = <
  TData = Awaited<ReturnType<typeof executeDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: ExecuteDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getExecuteDashboardQueryKey({ dashboardId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof executeDashboard>>> = ({ signal }) =>
    executeDashboard({ dashboardId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof executeDashboard>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ExecuteDashboardQueryResult = NonNullable<Awaited<ReturnType<typeof executeDashboard>>>;
export type ExecuteDashboardQueryError = ErrorType<TLHTTPValidationError>;

export function useExecuteDashboard<
  TData = Awaited<ReturnType<typeof executeDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExecuteDashboardPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeDashboard>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof executeDashboard>>,
          TError,
          Awaited<ReturnType<typeof executeDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExecuteDashboard<
  TData = Awaited<ReturnType<typeof executeDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExecuteDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeDashboard>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof executeDashboard>>,
          TError,
          Awaited<ReturnType<typeof executeDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExecuteDashboard<
  TData = Awaited<ReturnType<typeof executeDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExecuteDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Execute Dashboard
 */

export function useExecuteDashboard<
  TData = Awaited<ReturnType<typeof executeDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: ExecuteDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExecuteDashboardQueryOptions({ dashboardId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Execute Dashboard
 */
export const prefetchExecuteDashboardQuery = async <
  TData = Awaited<ReturnType<typeof executeDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: ExecuteDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExecuteDashboardQueryOptions({ dashboardId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Execute Dashboard
 */
export const invalidateExecuteDashboard = async (
  queryClient: QueryClient,
  { dashboardId }: ExecuteDashboardPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getExecuteDashboardQueryKey({ dashboardId }) }, options);

  return queryClient;
};

/**
 * @summary Get Dashboard Executions
 */
export const getDashboardExecutions = (
  { dashboardId }: GetDashboardExecutionsPathParameters,
  params?: GetDashboardExecutionsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGetDashboardExecutionsResponse>(
    { url: `/v2/data-harmonization/dashboards/${dashboardId}/executions`, method: 'GET', params, signal },
    options,
  );
};

export const getGetDashboardExecutionsQueryKey = (
  { dashboardId }: GetDashboardExecutionsPathParameters,
  params?: GetDashboardExecutionsParams,
) => {
  return ['v2', 'data-harmonization', 'dashboards', dashboardId, 'executions', ...(params ? [params] : [])] as const;
};

export const getGetDashboardExecutionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboardExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardExecutionsPathParameters,
  params?: GetDashboardExecutionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardExecutionsQueryKey({ dashboardId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardExecutions>>> = ({ signal }) =>
    getDashboardExecutions({ dashboardId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDashboardExecutions>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDashboardExecutionsQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardExecutions>>>;
export type GetDashboardExecutionsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDashboardExecutions<
  TData = Awaited<ReturnType<typeof getDashboardExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardExecutionsPathParameters,
  params: undefined | GetDashboardExecutionsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardExecutions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardExecutions>>,
          TError,
          Awaited<ReturnType<typeof getDashboardExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardExecutions<
  TData = Awaited<ReturnType<typeof getDashboardExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardExecutionsPathParameters,
  params?: GetDashboardExecutionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardExecutions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardExecutions>>,
          TError,
          Awaited<ReturnType<typeof getDashboardExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardExecutions<
  TData = Awaited<ReturnType<typeof getDashboardExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardExecutionsPathParameters,
  params?: GetDashboardExecutionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Dashboard Executions
 */

export function useGetDashboardExecutions<
  TData = Awaited<ReturnType<typeof getDashboardExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardExecutionsPathParameters,
  params?: GetDashboardExecutionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDashboardExecutionsQueryOptions({ dashboardId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Dashboard Executions
 */
export const prefetchGetDashboardExecutionsQuery = async <
  TData = Awaited<ReturnType<typeof getDashboardExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: GetDashboardExecutionsPathParameters,
  params?: GetDashboardExecutionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDashboardExecutionsQueryOptions({ dashboardId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Dashboard Executions
 */
export const invalidateGetDashboardExecutions = async (
  queryClient: QueryClient,
  { dashboardId }: GetDashboardExecutionsPathParameters,
  params?: GetDashboardExecutionsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetDashboardExecutionsQueryKey({ dashboardId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Dashboard Schedules
 */
export const getDashboardSchedules = (
  { dashboardId }: GetDashboardSchedulesPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardSchedulesResponse>(
    { url: `/v2/data-harmonization/dashboards/${dashboardId}/schedules`, method: 'GET', signal },
    options,
  );
};

export const getGetDashboardSchedulesQueryKey = ({ dashboardId }: GetDashboardSchedulesPathParameters) => {
  return ['v2', 'data-harmonization', 'dashboards', dashboardId, 'schedules'] as const;
};

export const getGetDashboardSchedulesQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboardSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardSchedulesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardSchedules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardSchedulesQueryKey({ dashboardId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardSchedules>>> = ({ signal }) =>
    getDashboardSchedules({ dashboardId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDashboardSchedules>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDashboardSchedulesQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardSchedules>>>;
export type GetDashboardSchedulesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDashboardSchedules<
  TData = Awaited<ReturnType<typeof getDashboardSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardSchedulesPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardSchedules>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardSchedules>>,
          TError,
          Awaited<ReturnType<typeof getDashboardSchedules>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardSchedules<
  TData = Awaited<ReturnType<typeof getDashboardSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardSchedulesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardSchedules>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardSchedules>>,
          TError,
          Awaited<ReturnType<typeof getDashboardSchedules>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardSchedules<
  TData = Awaited<ReturnType<typeof getDashboardSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardSchedulesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardSchedules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Dashboard Schedules
 */

export function useGetDashboardSchedules<
  TData = Awaited<ReturnType<typeof getDashboardSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardSchedulesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardSchedules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDashboardSchedulesQueryOptions({ dashboardId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Dashboard Schedules
 */
export const prefetchGetDashboardSchedulesQuery = async <
  TData = Awaited<ReturnType<typeof getDashboardSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: GetDashboardSchedulesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardSchedules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDashboardSchedulesQueryOptions({ dashboardId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Dashboard Schedules
 */
export const invalidateGetDashboardSchedules = async (
  queryClient: QueryClient,
  { dashboardId }: GetDashboardSchedulesPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetDashboardSchedulesQueryKey({ dashboardId }) }, options);

  return queryClient;
};

/**
 * @summary Create Dashboard Schedule
 */
export const createDashboardSchedule = (
  { dashboardId }: CreateDashboardSchedulePathParameters,
  tLDashboardScheduleCreateRequest: BodyType<TLDashboardScheduleCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardScheduleResponse>(
    {
      url: `/v2/data-harmonization/dashboards/${dashboardId}/schedules`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLDashboardScheduleCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateDashboardScheduleQueryKey = (
  { dashboardId }: CreateDashboardSchedulePathParameters,
  tLDashboardScheduleCreateRequest?: BodyType<TLDashboardScheduleCreateRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'dashboards',
    dashboardId,
    'schedules',
    tLDashboardScheduleCreateRequest,
  ] as const;
};

export const getCreateDashboardScheduleQueryOptions = <
  TData = Awaited<ReturnType<typeof createDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: CreateDashboardSchedulePathParameters,
  tLDashboardScheduleCreateRequest: BodyType<TLDashboardScheduleCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboardSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateDashboardScheduleQueryKey({ dashboardId }, tLDashboardScheduleCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createDashboardSchedule>>> = ({ signal }) =>
    createDashboardSchedule({ dashboardId }, tLDashboardScheduleCreateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createDashboardSchedule>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateDashboardScheduleQueryResult = NonNullable<Awaited<ReturnType<typeof createDashboardSchedule>>>;
export type CreateDashboardScheduleQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateDashboardSchedule<
  TData = Awaited<ReturnType<typeof createDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateDashboardSchedulePathParameters,
  tLDashboardScheduleCreateRequest: BodyType<TLDashboardScheduleCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboardSchedule>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createDashboardSchedule>>,
          TError,
          Awaited<ReturnType<typeof createDashboardSchedule>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateDashboardSchedule<
  TData = Awaited<ReturnType<typeof createDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateDashboardSchedulePathParameters,
  tLDashboardScheduleCreateRequest: BodyType<TLDashboardScheduleCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboardSchedule>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createDashboardSchedule>>,
          TError,
          Awaited<ReturnType<typeof createDashboardSchedule>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateDashboardSchedule<
  TData = Awaited<ReturnType<typeof createDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateDashboardSchedulePathParameters,
  tLDashboardScheduleCreateRequest: BodyType<TLDashboardScheduleCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboardSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Dashboard Schedule
 */

export function useCreateDashboardSchedule<
  TData = Awaited<ReturnType<typeof createDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: CreateDashboardSchedulePathParameters,
  tLDashboardScheduleCreateRequest: BodyType<TLDashboardScheduleCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboardSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateDashboardScheduleQueryOptions(
    { dashboardId },
    tLDashboardScheduleCreateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Dashboard Schedule
 */
export const prefetchCreateDashboardScheduleQuery = async <
  TData = Awaited<ReturnType<typeof createDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: CreateDashboardSchedulePathParameters,
  tLDashboardScheduleCreateRequest: BodyType<TLDashboardScheduleCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDashboardSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateDashboardScheduleQueryOptions(
    { dashboardId },
    tLDashboardScheduleCreateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Dashboard Schedule
 */
export const invalidateCreateDashboardSchedule = async (
  queryClient: QueryClient,
  { dashboardId }: CreateDashboardSchedulePathParameters,
  tLDashboardScheduleCreateRequest: BodyType<TLDashboardScheduleCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateDashboardScheduleQueryKey({ dashboardId }, tLDashboardScheduleCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Dashboard Schedule
 */
export const deleteDashboardSchedule = (
  { dashboardId, scheduleId }: DeleteDashboardSchedulePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/dashboards/${dashboardId}/schedules/${scheduleId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteDashboardScheduleQueryKey = ({
  dashboardId,
  scheduleId,
}: DeleteDashboardSchedulePathParameters) => {
  return ['DELETE', 'v2', 'data-harmonization', 'dashboards', dashboardId, 'schedules', scheduleId] as const;
};

export const getDeleteDashboardScheduleQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId, scheduleId }: DeleteDashboardSchedulePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteDashboardScheduleQueryKey({ dashboardId, scheduleId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteDashboardSchedule>>> = ({ signal }) =>
    deleteDashboardSchedule({ dashboardId, scheduleId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined && scheduleId !== null && scheduleId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardSchedule>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteDashboardScheduleQueryResult = NonNullable<Awaited<ReturnType<typeof deleteDashboardSchedule>>>;
export type DeleteDashboardScheduleQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteDashboardSchedule<
  TData = Awaited<ReturnType<typeof deleteDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDashboardSchedulePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardSchedule>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteDashboardSchedule>>,
          TError,
          Awaited<ReturnType<typeof deleteDashboardSchedule>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteDashboardSchedule<
  TData = Awaited<ReturnType<typeof deleteDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDashboardSchedulePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardSchedule>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteDashboardSchedule>>,
          TError,
          Awaited<ReturnType<typeof deleteDashboardSchedule>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteDashboardSchedule<
  TData = Awaited<ReturnType<typeof deleteDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDashboardSchedulePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Dashboard Schedule
 */

export function useDeleteDashboardSchedule<
  TData = Awaited<ReturnType<typeof deleteDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId, scheduleId }: DeleteDashboardSchedulePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteDashboardScheduleQueryOptions({ dashboardId, scheduleId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Dashboard Schedule
 */
export const prefetchDeleteDashboardScheduleQuery = async <
  TData = Awaited<ReturnType<typeof deleteDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId, scheduleId }: DeleteDashboardSchedulePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDashboardSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteDashboardScheduleQueryOptions({ dashboardId, scheduleId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Dashboard Schedule
 */
export const invalidateDeleteDashboardSchedule = async (
  queryClient: QueryClient,
  { dashboardId, scheduleId }: DeleteDashboardSchedulePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getDeleteDashboardScheduleQueryKey({ dashboardId, scheduleId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Dashboard Schedule
 */
export const updateDashboardSchedule = (
  { dashboardId, scheduleId }: UpdateDashboardSchedulePathParameters,
  tLDashboardScheduleUpdateRequest: BodyType<TLDashboardScheduleUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardScheduleResponse>(
    {
      url: `/v2/data-harmonization/dashboards/${dashboardId}/schedules/${scheduleId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLDashboardScheduleUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateDashboardScheduleQueryKey = (
  { dashboardId, scheduleId }: UpdateDashboardSchedulePathParameters,
  tLDashboardScheduleUpdateRequest?: BodyType<TLDashboardScheduleUpdateRequest>,
) => {
  return [
    'PATCH',
    'v2',
    'data-harmonization',
    'dashboards',
    dashboardId,
    'schedules',
    scheduleId,
    tLDashboardScheduleUpdateRequest,
  ] as const;
};

export const getUpdateDashboardScheduleQueryOptions = <
  TData = Awaited<ReturnType<typeof updateDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId, scheduleId }: UpdateDashboardSchedulePathParameters,
  tLDashboardScheduleUpdateRequest: BodyType<TLDashboardScheduleUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getUpdateDashboardScheduleQueryKey({ dashboardId, scheduleId }, tLDashboardScheduleUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateDashboardSchedule>>> = ({ signal }) =>
    updateDashboardSchedule({ dashboardId, scheduleId }, tLDashboardScheduleUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined && scheduleId !== null && scheduleId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateDashboardSchedule>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateDashboardScheduleQueryResult = NonNullable<Awaited<ReturnType<typeof updateDashboardSchedule>>>;
export type UpdateDashboardScheduleQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateDashboardSchedule<
  TData = Awaited<ReturnType<typeof updateDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDashboardSchedulePathParameters,
  tLDashboardScheduleUpdateRequest: BodyType<TLDashboardScheduleUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardSchedule>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDashboardSchedule>>,
          TError,
          Awaited<ReturnType<typeof updateDashboardSchedule>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDashboardSchedule<
  TData = Awaited<ReturnType<typeof updateDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDashboardSchedulePathParameters,
  tLDashboardScheduleUpdateRequest: BodyType<TLDashboardScheduleUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardSchedule>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDashboardSchedule>>,
          TError,
          Awaited<ReturnType<typeof updateDashboardSchedule>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDashboardSchedule<
  TData = Awaited<ReturnType<typeof updateDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDashboardSchedulePathParameters,
  tLDashboardScheduleUpdateRequest: BodyType<TLDashboardScheduleUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Dashboard Schedule
 */

export function useUpdateDashboardSchedule<
  TData = Awaited<ReturnType<typeof updateDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId, scheduleId }: UpdateDashboardSchedulePathParameters,
  tLDashboardScheduleUpdateRequest: BodyType<TLDashboardScheduleUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateDashboardScheduleQueryOptions(
    { dashboardId, scheduleId },
    tLDashboardScheduleUpdateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Dashboard Schedule
 */
export const prefetchUpdateDashboardScheduleQuery = async <
  TData = Awaited<ReturnType<typeof updateDashboardSchedule>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId, scheduleId }: UpdateDashboardSchedulePathParameters,
  tLDashboardScheduleUpdateRequest: BodyType<TLDashboardScheduleUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDashboardSchedule>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateDashboardScheduleQueryOptions(
    { dashboardId, scheduleId },
    tLDashboardScheduleUpdateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Dashboard Schedule
 */
export const invalidateUpdateDashboardSchedule = async (
  queryClient: QueryClient,
  { dashboardId, scheduleId }: UpdateDashboardSchedulePathParameters,
  tLDashboardScheduleUpdateRequest: BodyType<TLDashboardScheduleUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateDashboardScheduleQueryKey({ dashboardId, scheduleId }, tLDashboardScheduleUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Dashboard Storage Status
 */
export const getDashboardStorageStatus = (
  { dashboardId }: GetDashboardStorageStatusPathParameters,
  params?: GetDashboardStorageStatusParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDashboardStorageStatusResponse>(
    { url: `/v2/data-harmonization/dashboards/${dashboardId}/storage-status`, method: 'GET', params, signal },
    options,
  );
};

export const getGetDashboardStorageStatusQueryKey = (
  { dashboardId }: GetDashboardStorageStatusPathParameters,
  params?: GetDashboardStorageStatusParams,
) => {
  return [
    'v2',
    'data-harmonization',
    'dashboards',
    dashboardId,
    'storage-status',
    ...(params ? [params] : []),
  ] as const;
};

export const getGetDashboardStorageStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboardStorageStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardStorageStatusPathParameters,
  params?: GetDashboardStorageStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardStorageStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardStorageStatusQueryKey({ dashboardId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardStorageStatus>>> = ({ signal }) =>
    getDashboardStorageStatus({ dashboardId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDashboardStorageStatus>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDashboardStorageStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardStorageStatus>>>;
export type GetDashboardStorageStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDashboardStorageStatus<
  TData = Awaited<ReturnType<typeof getDashboardStorageStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardStorageStatusPathParameters,
  params: undefined | GetDashboardStorageStatusParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardStorageStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardStorageStatus>>,
          TError,
          Awaited<ReturnType<typeof getDashboardStorageStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardStorageStatus<
  TData = Awaited<ReturnType<typeof getDashboardStorageStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardStorageStatusPathParameters,
  params?: GetDashboardStorageStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardStorageStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardStorageStatus>>,
          TError,
          Awaited<ReturnType<typeof getDashboardStorageStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDashboardStorageStatus<
  TData = Awaited<ReturnType<typeof getDashboardStorageStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDashboardStorageStatusPathParameters,
  params?: GetDashboardStorageStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardStorageStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Dashboard Storage Status
 */

export function useGetDashboardStorageStatus<
  TData = Awaited<ReturnType<typeof getDashboardStorageStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetDashboardStorageStatusPathParameters,
  params?: GetDashboardStorageStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardStorageStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDashboardStorageStatusQueryOptions({ dashboardId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Dashboard Storage Status
 */
export const prefetchGetDashboardStorageStatusQuery = async <
  TData = Awaited<ReturnType<typeof getDashboardStorageStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: GetDashboardStorageStatusPathParameters,
  params?: GetDashboardStorageStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardStorageStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDashboardStorageStatusQueryOptions({ dashboardId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Dashboard Storage Status
 */
export const invalidateGetDashboardStorageStatus = async (
  queryClient: QueryClient,
  { dashboardId }: GetDashboardStorageStatusPathParameters,
  params?: GetDashboardStorageStatusParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetDashboardStorageStatusQueryKey({ dashboardId }, params) },
    options,
  );

  return queryClient;
};
